import { useContext } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import { FileFieldRetryContext } from './FileField.jsx';

/**
 * A retry button for failed file uploads, for use inside a FileField itemTemplate.
 *
 * Reads the `_localId` and `_canRetry` keys from TemplateContext to determine
 * whether and which file to retry. Silently renders nothing when retry is not
 * applicable (successful uploads, items without _canRetry).
 *
 * YAML usage (inside itemTemplate):
 *   - type: FileFieldRetry
 *     content: "Retry upload"
 *     attributes:
 *       class: text-sm text-red-600 underline cursor-pointer
 */
export const FileFieldRetry = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const retryUpload = useContext(FileFieldRetryContext);
    const attributes = useEvaluatedAttributes(props.attributes);

    const canRetry = evaluateTemplateValue({
        valueToEvaluate: '~._canRetry',
        globalDataContext,
        templateContext,
    });

    const localId = evaluateTemplateValue({
        valueToEvaluate: '~._localId',
        globalDataContext,
        templateContext,
    });

    if (!canRetry || !localId || !retryUpload) return null;

    const label = evaluateTemplateValue({
        valueToEvaluate: props.content,
        globalDataContext,
        templateContext,
    }) ?? 'Retry';

    return (
        <ActionDependant {...props}>
            <button
                type="button"
                onClick={() => retryUpload(localId)}
                {...attributes}
            >
                {label}
            </button>
        </ActionDependant>
    );
};
